import type {
  AutocompleteData,
  ColoredMaterialAutocompleteProperties,
} from "@color-finder/color-finder-api/models";
import CheckBoxOutlineBlankSharp from "@mui/icons-material/CheckBoxOutlineBlankSharp";
import CheckBoxSharp from "@mui/icons-material/CheckBoxSharp";
import { Autocomplete, Checkbox, TextField, Typography } from "@mui/material";
import type { AutocompleteRenderOptionState } from "@mui/material/Autocomplete/Autocomplete";
import { useAuthUser } from "hooks/authentication";
import { useAutocompleteOptions } from "hooks/autocomplete-data";
import type { FilterEntry } from "hooks/filters";
import { useLatch } from "hooks/latch";
import {
  getApplicationByClass,
  getClassesByIndustry,
  getFilterName,
  getIndustrySelect,
} from "hooks/utils/material-data";
import type { CdcUserAttributes } from "models/cdcUser";
import {
  SettingsContext,
  type SettingsContextValue,
} from "providers/SettingsProvider";
import type React from "react";
import {
  type Dispatch,
  type KeyboardEventHandler,
  type ReactNode,
  useContext,
  useRef,
  useState,
} from "react";
import { getDataFromLocalstorageByKey } from "service/utils/get-data-localstorage";
import { useAppConfig } from "../../../config/app-config";
import { useDebounce } from "../../../hooks/utils/debounce";
import { AutocompleteErrorMessage } from "./AutocompleteErrorMessage";

export interface CovestroAutocompleteProps {
  type:
    | ColoredMaterialAutocompleteProperties
    | "industry"
    | "applicationClass"
    | "application";
  label: string;
  example?: string;
  value: Array<AutocompleteData>;
  onChange?: Dispatch<Array<AutocompleteData>>;
  checkbox?: boolean;
  limit?: number;
  filters?: Array<FilterEntry>;
  formatOptionText?: (option: AutocompleteData) => ReactNode;
  formatOptionLabel?: (option: AutocompleteData) => string;
  isDisabled?: boolean;
}

export const ColorSearchAutocomplete = ({
  type,
  label,
  value,
  example,
  onChange,
  checkbox,
  formatOptionText = (option) => option.name,
  formatOptionLabel = (option) => option.name,
  limit = 21,
  filters,
  isDisabled = false,
}: CovestroAutocompleteProps) => {
  const [query, setQuery] = useState<string>();
  const [open, setOpen] = useState(false);
  const wasOpen = useLatch(open);

  const { settings, setSettings, hierachyData } =
    useContext<SettingsContextValue>(SettingsContext);
  const isSingleFilterSearch =
    type === "industry" ||
    type === "applicationClass" ||
    type === "application" ||
    type === "yearOfDevelopment";
  const isUseCaseFilter =
    type === "industry" ||
    type === "applicationClass" ||
    type === "application";

  const [appConfig] = useAppConfig();
  const debouncedQuery = useDebounce(
    query,
    appConfig.ux.general.inputDebounceTimeMs,
  );
  const [options, error, isLoading] = useAutocompleteOptions(
    type,
    debouncedQuery,
    limit,
    wasOpen && !isUseCaseFilter,
    filters,
  );
  const cdcUser: CdcUserAttributes | undefined =
    getDataFromLocalstorageByKey("cdcUser");
  const { user } = useAuthUser();

  const isLogin = Boolean(user?.email) || Boolean(cdcUser);

  const getOption = (typeName: string, isUseCaseFilter?: boolean) => {
    if (!isUseCaseFilter) {
      return options ?? [];
    } else {
      if (typeName === "industry") {
        const listIndustry = getIndustrySelect(hierachyData);
        return listIndustry;
      } else if (type === "applicationClass" && filters) {
        const indName = getFilterName(
          "industry",
          undefined,
          undefined,
          filters,
        );
        return indName
          ? getClassesByIndustry(hierachyData, indName.industryName)
          : [];
      } else if (type === "application" && filters) {
        const indName = getFilterName(
          "industry",
          "applicationClass",
          undefined,
          filters,
        );
        const applicationList = indName
          ? getApplicationByClass(
              indName.industryName,
              indName.applicationClassName,
              hierachyData,
            )
          : [];
        return Array.isArray(applicationList) && applicationList.length > 0
          ? applicationList.map((app) => ({
              name: app.application,
              value: app.application,
            }))
          : [];
      } else {
        return options ?? [];
      }
    }
  };

  const moreOptions = {
    name: "... there are more options available. Type to filter the list.",
    value: "",
  };

  const renderItem = checkbox ? renderCheckboxItem : renderTextItem;

  const filterOptions = (options: Array<AutocompleteData>) => {
    const filtered: Array<AutocompleteData> = [...options];
    if (filtered.length === limit) {
      filtered.pop();
      filtered.push(moreOptions);
    }
    return filtered;
  };

  const addQueryAsValue = () => {
    if (query && onChange) {
      const q = (query || "").replace("**", "*");
      onChange([...value, { name: q, value: q }]);
      setQuery("");
    }
  };

  const handleInputKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter") {
      addQueryAsValue();
    }
  };

  const handleOpenInputSearch = (isOpen: boolean) => {
    setOpen(isOpen);
    setSettings({ ...settings, inputTypingSearch: isOpen });
  };

  const justSelectedRef = useRef("");

  // groupBy must stay stable across renders: MUI caches the last shown option
  // list and reuses it during the popup close transition, so if groupBy flips
  // while that cache is stale it renders the wrong shape and throws in its group
  // renderer (option.options.map on undefined). Latch grouped-ness from the last
  // non-empty result so a transient empty ("No options") list can't toggle it —
  // note [].every() is vacuously true, which is exactly what used to flip it on.
  const isGroupedRef = useRef(false);
  if (options && options.length > 0) {
    isGroupedRef.current = options.every((o) => Boolean(o.groupName));
  }

  const handleCloseAutocomplete = (
    _event: React.SyntheticEvent,
    reason: string,
  ) => {
    if (reason === "blur" && justSelectedRef.current === "onChange") {
      justSelectedRef.current = "handleCloseAutocomplete";
      handleOpenInputSearch(false);
      return;
    }

    if (reason === "blur") {
      addQueryAsValue();
    }

    handleOpenInputSearch(false);
  };

  return (
    <Autocomplete
      sx={{
        backgroundColor: isDisabled ? "#f5f5f5" : "inherit",
        "& .MuiInputBase-root": { borderRadius: "4px" },
      }}
      fullWidth
      disabled={isDisabled}
      renderInput={(params) => (
        <TextField
          {...params}
          onKeyDown={handleInputKeyDown}
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {example && !value.length && open && (
                  <Typography
                    onClick={() => setOpen((prev) => !prev)}
                    color="text.disabled"
                    fontSize="var(--body-b3-12-regular-size, 12px)"
                    fontStyle="italic"
                  >
                    e.g. {example}
                  </Typography>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          inputProps={{
            ...params.inputProps,
            readOnly: checkbox,
            style: { minWidth: 0 },
          }}
        />
      )}
      renderOption={(props, option: AutocompleteData, state) =>
        option === moreOptions ? (
          <li {...props}>
            <Typography fontSize="var(--body-b2-14-regular-size, 14px)">
              {option.name}
            </Typography>
          </li>
        ) : (
          renderItem(props, formatOptionText(option), state)
        )
      }
      multiple={true}
      blurOnSelect={!checkbox}
      disableCloseOnSelect={checkbox}
      value={value}
      loading={isLoading}
      open={open}
      onOpen={() => handleOpenInputSearch(true)}
      onClose={handleCloseAutocomplete}
      onChange={(_, items) => {
        if (items.length > 1 && isSingleFilterSearch) {
          onChange?.([...[items[1]]]);
        } else {
          onChange?.([...items]);
        }
        if (type === "brand" || type === "cdc") {
          setSettings({ ...settings, [type]: [...items] });
        }
        justSelectedRef.current = "onChange";
      }}
      onInputChange={(_e, value) => setQuery(value)}
      getOptionDisabled={(option) => option === moreOptions}
      options={getOption(type, isUseCaseFilter)}
      groupBy={
        !isSingleFilterSearch && isGroupedRef.current
          ? (val) => val.groupName!
          : undefined
      }
      filterOptions={filterOptions}
      getOptionLabel={formatOptionLabel}
      noOptionsText={
        error ? (
          <AutocompleteErrorMessage isLogin={isLogin} error={error} />
        ) : undefined
      }
      isOptionEqualToValue={(option, value) => option.value === value.value}
    />
  );
};

const renderCheckboxItem = (
  props: React.HTMLAttributes<HTMLLIElement>,
  optionText: ReactNode,
  state: AutocompleteRenderOptionState,
) => (
  <li {...props}>
    <Checkbox
      icon={<CheckBoxOutlineBlankSharp fontSize="small" />}
      checkedIcon={<CheckBoxSharp fontSize="small" />}
      style={{ marginRight: 8 }}
      checked={state.selected}
    />
    {optionText}
  </li>
);

const renderTextItem = (
  props: React.HTMLAttributes<HTMLLIElement>,
  optionText: ReactNode,
) => <li {...props}>{optionText}</li>;
